import { useEffect, useRef, useCallback } from "react";

export function usePolling(
  callback: () => void | Promise<void>,
  interval: number,
  enabled = true,
): void {
  const savedCallback = useRef(callback);

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return;

    const tick = () => {
      // Skip poll when tab is hidden
      if (document.visibilityState === "hidden") return;
      savedCallback.current();
    };

    // Execute immediately on enable
    tick();

    // Set up interval
    const id = setInterval(tick, interval);

    return () => clearInterval(id);
  }, [interval, enabled]);
}

// Hook variant that returns controls
interface UsePollingControlsReturn {
  start: () => void;
  stop: () => void;
  isPolling: boolean;
}

export function usePollingWithControls(
  callback: () => void | Promise<void>,
  interval: number,
): UsePollingControlsReturn {
  const savedCallback = useRef(callback);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const start = useCallback(() => {
    if (isPollingRef.current) return;

    isPollingRef.current = true;
    savedCallback.current(); // Execute immediately

    intervalRef.current = setInterval(() => {
      // Skip poll when tab is hidden
      if (document.visibilityState === "hidden") return;
      savedCallback.current();
    }, interval);
  }, [interval]);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    start,
    stop,
    isPolling: isPollingRef.current,
  };
}

export default usePolling;
